/**
 * formatters.ts
 *
 * Intl-based formatting layer for dates, times, currencies, and relative timestamps.
 * Preserves workspace IANA timezones while localizing text representations to user locale.
 */

import { type Bcp47LocaleTag, SUPPORTED_LOCALES } from './locale-contracts.js';

export function formatDate(
  timestampMs: number | string | Date,
  locale: Bcp47LocaleTag = 'en',
  timeZone = 'UTC',
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }
): string {
  const date = typeof timestampMs === 'string' || typeof timestampMs === 'number'
    ? new Date(timestampMs)
    : timestampMs;

  const targetLocale = SUPPORTED_LOCALES[locale]?.dateTimeLocale || 'en-US';
  return new Intl.DateTimeFormat(targetLocale, { ...options, timeZone }).format(date);
}

export function formatTime(
  timestampMs: number | string | Date,
  locale: Bcp47LocaleTag = 'en',
  timeZone = 'UTC',
  options: Intl.DateTimeFormatOptions = { timeStyle: 'short' }
): string {
  const date = typeof timestampMs === 'string' || typeof timestampMs === 'number'
    ? new Date(timestampMs)
    : timestampMs;

  const targetLocale = SUPPORTED_LOCALES[locale]?.dateTimeLocale || 'en-US';
  return new Intl.DateTimeFormat(targetLocale, { ...options, timeZone }).format(date);
}

export function formatDateTime(
  timestampMs: number | string | Date,
  locale: Bcp47LocaleTag = 'en',
  timeZone = 'UTC'
): string {
  return `${formatDate(timestampMs, locale, timeZone)} ${formatTime(timestampMs, locale, timeZone)}`;
}

export function formatCurrency(
  amount: number,
  currency = 'USD',
  locale: Bcp47LocaleTag = 'en'
): string {
  const targetLocale = SUPPORTED_LOCALES[locale]?.dateTimeLocale || 'en-US';
  return new Intl.NumberFormat(targetLocale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatNumber(
  value: number,
  locale: Bcp47LocaleTag = 'en',
  options?: Intl.NumberFormatOptions
): string {
  const targetLocale = SUPPORTED_LOCALES[locale]?.dateTimeLocale || 'en-US';
  return new Intl.NumberFormat(targetLocale, options).format(value);
}

export function formatRelativeTime(
  diffValue: number,
  unit: Intl.RelativeTimeFormatUnit,
  locale: Bcp47LocaleTag = 'en'
): string {
  const targetLocale = SUPPORTED_LOCALES[locale]?.dateTimeLocale || 'en-US';
  return new Intl.RelativeTimeFormat(targetLocale, { numeric: 'auto' }).format(diffValue, unit);
}
